// Modal behavior adapted from BootstrapVue 2.21.2; see BOOTSTRAPVUE-LICENSE (MIT).
import {
  h,
  defineComponent,
  ref,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  Teleport,
  useId,
  createApp,
  getCurrentInstance
} from 'vue'
import { BButton } from './primitives'
import {
  modalStack,
  registerModal,
  unregisterModal,
  baseZIndex,
  scrollbarWidth,
  bodyOverflowing
} from './modal-manager'
export const modalRegistry = new Map()
const classProp = [String, Array, Object]
const frame = () => new Promise((resolve) => requestAnimationFrame(resolve))
const focus = (element) => {
  if (element && !element.disabled) element.focus?.({ preventScroll: true })
}
const resolveElement = (element) =>
  typeof element === 'string' ? document.querySelector(element) : element?.$el || element
// The modal's own opacity transition controls lifecycle timing, as in BootstrapVue.
const transitionTime = (element) => {
  const style = getComputedStyle(element)
  const durations = style.transitionDuration
    .split(',')
    .map((value) => parseFloat(value) * (value.includes('ms') ? 1 : 1000))
  const delays = style.transitionDelay
    .split(',')
    .map((value) => parseFloat(value) * (value.includes('ms') ? 1 : 1000))
  return Math.max(0, ...durations.map((value, index) => value + delays[index % delays.length]))
}
export const BModal = defineComponent({
  inheritAttrs: false,
  props: {
    id: String,
    ariaLabel: String,
    modelValue: Boolean,
    visible: Boolean,
    title: String,
    size: { default: 'md' },
    centered: Boolean,
    scrollable: Boolean,
    hideHeader: Boolean,
    hideFooter: Boolean,
    hideHeaderClose: Boolean,
    hideBackdrop: Boolean,
    noCloseOnBackdrop: Boolean,
    noCloseOnEsc: Boolean,
    noEnforceFocus: Boolean,
    noFade: Boolean,
    noStacking: Boolean,
    static: Boolean,
    lazy: Boolean,
    returnFocus: [String, Object],
    autoFocusButton: String,
    ignoreEnforceFocusSelector: [String, Array],
    okOnly: Boolean,
    okTitle: { default: 'OK' },
    cancelTitle: { default: 'Cancel' },
    buttonSize: String,
    cancelVariant: { default: 'secondary' },
    okVariant: { default: 'primary' },
    okDisabled: Boolean,
    cancelDisabled: Boolean,
    busy: Boolean,
    headerCloseLabel: { default: 'Close' },
    titleTag: { default: 'h5' },
    titleSrOnly: Boolean,
    modalClass: classProp,
    dialogClass: classProp,
    contentClass: classProp,
    titleClass: classProp,
    headerClass: classProp,
    bodyClass: classProp,
    footerClass: classProp,
    headerBgVariant: String,
    headerTextVariant: String,
    headerBorderVariant: String,
    bodyBgVariant: String,
    bodyTextVariant: String,
    footerBgVariant: String,
    footerTextVariant: String,
    footerBorderVariant: String
  },
  emits: [
    'update:modelValue',
    'change',
    'show',
    'shown',
    'hide',
    'hidden',
    'ok',
    'cancel',
    'close'
  ],
  setup(p, { attrs, slots, emit, expose }) {
    const modal = ref(),
      content = ref(),
      topTrap = ref(),
      bottomTrap = ref()
    const rendered = ref(false),
      visible = ref(false),
      shown = ref(false),
      overflowing = ref(false)
    const id = p.id || useId()
    const instance = getCurrentInstance()
    let phase = 'hidden',
      previousFocus,
      generation = 0,
      destroyed = false,
      reopen = false
    let timer,
      finishTransition,
      observer,
      dragStarted = false,
      ignoreBackdrop = false
    const model = (value) => {
      emit('update:modelValue', value)
      emit('change', value)
    }
    const event = (type, cancelable = false, trigger = null) => ({
      type,
      cancelable,
      trigger,
      target: modal.value || null,
      relatedTarget: null,
      vueTarget: instance.proxy,
      componentId: id,
      defaultPrevented: false,
      preventDefault() {
        if (this.cancelable) this.defaultPrevented = true
      }
    })
    const waitTransition = () =>
      new Promise((resolve) => {
        finishTransition = resolve
        timer = setTimeout(
          () => {
            finishTransition = null
            resolve()
          },
          p.noFade ? 0 : transitionTime(modal.value) + 5
        )
      })
    const stopTransition = () => {
      clearTimeout(timer)
      finishTransition?.()
      finishTransition = null
    }
    const top = () => modalStack.at(-1) === entry
    const checkOverflow = () => {
      if (modal.value)
        overflowing.value = modal.value.scrollHeight > document.documentElement.clientHeight
    }
    const tabables = () =>
      [...content.value.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
        .filter(
          (el) =>
            el.tabIndex >= 0 &&
            !el.matches(':disabled') &&
            el.getClientRects().length &&
            getComputedStyle(el).visibility !== 'hidden'
        )
        .sort((a, b) => (a.tabIndex || Infinity) - (b.tabIndex || Infinity))
    const enforceFocus = (e) => {
      if (phase !== 'shown' || !top() || p.noEnforceFocus || content.value?.contains(e.target))
        return
      const ignored = [p.ignoreEnforceFocusSelector].flat().filter(Boolean).join(',')
      if (ignored && e.target.closest?.(ignored)) return
      const nodes = tabables()
      focus(
        e.target === topTrap.value
          ? nodes.at(-1) || content.value
          : e.target === bottomTrap.value
          ? nodes[0] || content.value
          : content.value
      )
    }
    const initialFocus = () => {
      if (!top() || content.value?.contains(document.activeElement)) return
      const target =
        p.autoFocusButton &&
        content.value?.querySelector(`[data-modal-button="${p.autoFocusButton}"]`)
      focus(target || content.value)
      if (!target && modal.value) modal.value.scrollTop = 0
    }
    const hide = async (trigger = '') => {
      if (!visible.value || phase === 'closing') return
      const priorPhase = phase
      phase = 'closing'
      const e = event('hide', trigger !== 'FORCE', trigger || null)
      if (['ok', 'cancel', 'close'].includes(trigger)) emit(trigger, e)
      emit('hide', e)
      if (e.defaultPrevented) {
        phase = priorPhase
        model(true)
        return
      }
      const token = ++generation
      phase = 'closing'
      stopTransition()
      observer?.disconnect()
      visible.value = false
      shown.value = false
      model(false)
      await nextTick()
      await waitTransition()
      if (destroyed || token !== generation) return
      rendered.value = false
      phase = 'hidden'
      unregisterModal(entry)
      await nextTick()
      focus(resolveElement(p.returnFocus || previousFocus))
      previousFocus = null
      emit('hidden', event('hidden'))
      if (reopen) {
        reopen = false
        show()
      }
    }
    const show = async () => {
      if (destroyed || visible.value || phase === 'opening') return
      if (phase === 'closing') {
        reopen = true
        return
      }
      phase = 'opening'
      previousFocus ||= document.activeElement
      const e = event('show', true)
      emit('show', e)
      if (e.defaultPrevented) {
        phase = 'hidden'
        previousFocus = null
        model(false)
        return
      }
      const token = ++generation
      phase = 'opening'
      registerModal(entry)
      rendered.value = true
      visible.value = true
      model(true)
      await nextTick()
      await frame()
      await frame()
      if (destroyed || token !== generation) return
      shown.value = true
      await nextTick()
      await waitTransition()
      if (destroyed || token !== generation) return
      phase = 'shown'
      checkOverflow()
      observer = new ResizeObserver(checkOverflow)
      observer.observe(content.value)
      emit('shown', event('shown'))
      await nextTick()
      if (visible.value) initialFocus()
    }
    const toggle = (triggerElement) => {
      if (triggerElement) previousFocus = resolveElement(triggerElement)
      return visible.value ? hide() : show()
    }
    const entry = { show, hide, toggle, noStacking: () => p.noStacking }
    expose({ show, hide, toggle })
    onMounted(() => {
      modalRegistry.set(id, entry)
      document.addEventListener('focusin', enforceFocus)
      window.addEventListener('resize', checkOverflow)
      window.addEventListener('orientationchange', checkOverflow)
      if (p.modelValue || p.visible) show()
    })
    onBeforeUnmount(() => {
      destroyed = true
      generation++
      stopTransition()
      observer?.disconnect()
      document.removeEventListener('focusin', enforceFocus)
      window.removeEventListener('resize', checkOverflow)
      window.removeEventListener('orientationchange', checkOverflow)
      const restoreFocus = top() && rendered.value
      unregisterModal(entry)
      if (restoreFocus) focus(resolveElement(p.returnFocus || previousFocus))
      modalRegistry.delete(id)
    })
    watch(
      () => p.modelValue,
      (value) => (value ? show() : hide())
    )
    watch(
      () => p.visible,
      (value) => (value ? show() : hide())
    )
    const variants = (section) => [
      p[`${section}Class`],
      p[`${section}BgVariant`] && `bg-${p[`${section}BgVariant`]}`,
      p[`${section}TextVariant`] && `text-${p[`${section}TextVariant`]}`,
      p[`${section}BorderVariant`] && `border-${p[`${section}BorderVariant`]}`
    ]
    return () => {
      if (!rendered.value && (!p.static || p.lazy)) return null
      const scope = {
        visible: visible.value,
        hide,
        ok: () => hide('ok'),
        cancel: () => hide('cancel'),
        close: () => hide('close')
      }
      const button = (type, disabled, variant, title) =>
        h(
          BButton,
          {
            'data-modal-button': type,
            size: p.buttonSize,
            disabled: disabled || p.busy,
            variant,
            onClick: scope[type]
          },
          () => slots[`modal-${type}`]?.() || title
        )
      const children = [
        !p.hideHeader &&
          h(
            'header',
            { class: ['modal-header', variants('header')], id: `${id}___BV_modal_header_` },
            slots['modal-header']?.(scope) || [
              h(
                p.titleTag,
                {
                  class: ['modal-title', p.titleSrOnly && 'sr-only', p.titleClass],
                  id: `${id}___BV_modal_title_`
                },
                slots['modal-title']?.(scope) || p.title
              ),
              !p.hideHeaderClose &&
                h(
                  'button',
                  {
                    type: 'button',
                    class: 'close',
                    'data-modal-button': 'close',
                    'aria-label': p.headerCloseLabel,
                    onClick: scope.close
                  },
                  slots['modal-header-close']?.() || h('span', { 'aria-hidden': 'true' }, '×')
                )
            ]
          ),
        h(
          'div',
          { class: ['modal-body', variants('body')], id: `${id}___BV_modal_body_` },
          slots.default?.(scope)
        ),
        !p.hideFooter &&
          h(
            'footer',
            { class: ['modal-footer', variants('footer')], id: `${id}___BV_modal_footer_` },
            slots['modal-footer']?.(scope) || [
              !p.okOnly && button('cancel', p.cancelDisabled, p.cancelVariant, p.cancelTitle),
              button('ok', p.okDisabled, p.okVariant, p.okTitle)
            ]
          )
      ]
      const tree = h(
        'div',
        {
          style: {
            position: 'absolute',
            zIndex: baseZIndex + Math.max(0, modalStack.indexOf(entry))
          }
        },
        [
          h(
            'div',
            {
              ...attrs,
              id,
              ref: modal,
              role: 'dialog',
              tabindex: -1,
              'aria-modal': visible.value ? 'true' : undefined,
              'aria-hidden': visible.value ? undefined : 'true',
              'aria-label': p.ariaLabel || attrs['aria-label'],
              'aria-labelledby':
                !p.hideHeader &&
                (p.title || slots['modal-title']) &&
                !p.ariaLabel &&
                !attrs['aria-label']
                  ? `${id}___BV_modal_title_`
                  : undefined,
              'aria-describedby': `${id}___BV_modal_body_`,
              class: [
                'modal',
                !p.noFade && 'fade',
                shown.value && 'show',
                p.modalClass,
                attrs.class
              ],
              style: [
                attrs.style,
                {
                  display: rendered.value ? 'block' : 'none',
                  paddingLeft: !bodyOverflowing && overflowing.value ? `${scrollbarWidth}px` : '',
                  paddingRight: bodyOverflowing && !overflowing.value ? `${scrollbarWidth}px` : ''
                }
              ],
              onKeydown: (e) => {
                if (e.key === 'Escape' && top() && !p.noCloseOnEsc) hide('esc')
              },
              onMouseup: (e) => {
                ignoreBackdrop = dragStarted && e.target === modal.value
                dragStarted = false
              },
              onClick: (e) => {
                if (ignoreBackdrop) {
                  ignoreBackdrop = false
                  return
                }
                if (
                  top() &&
                  !p.noCloseOnBackdrop &&
                  document.body.contains(e.target) &&
                  !content.value?.contains(e.target)
                )
                  hide('backdrop')
              }
            },
            [
              h(
                'div',
                {
                  class: [
                    'modal-dialog',
                    `modal-${p.size}`,
                    p.centered && 'modal-dialog-centered',
                    p.scrollable && 'modal-dialog-scrollable',
                    p.dialogClass
                  ],
                  onMousedown: () => {
                    dragStarted = true
                  }
                },
                [
                  visible.value && !p.noEnforceFocus && h('span', { tabindex: 0, ref: topTrap }),
                  h(
                    'div',
                    {
                      class: ['modal-content', p.contentClass],
                      id: `${id}___BV_modal_content_`,
                      tabindex: -1,
                      ref: content
                    },
                    children
                  ),
                  visible.value && !p.noEnforceFocus && h('span', { tabindex: 0, ref: bottomTrap })
                ]
              )
            ]
          ),
          rendered.value &&
            !p.hideBackdrop &&
            h(
              'div',
              {
                id: `${id}___BV_modal_backdrop_`,
                class: ['modal-backdrop', !p.noFade && 'fade', shown.value && 'show']
              },
              slots['modal-backdrop']?.()
            )
        ]
      )
      return p.static ? tree : h(Teleport, { to: 'body' }, tree)
    }
  }
})
export const modalService = {
  show: (id) => modalRegistry.get(id)?.show(),
  hide: (id) => modalRegistry.get(id)?.hide('event'),
  toggle: (id, triggerElement) => modalRegistry.get(id)?.toggle(triggerElement),
  msgBoxConfirm(message, options = {}) {
    return new Promise((resolve) => {
      const root = document.createElement('div')
      document.body.append(root)
      let result = null
      const app = createApp({
        render: () =>
          h(
            BModal,
            {
              hideHeaderClose: true,
              ...options,
              modelValue: true,
              onOk: () => (result = true),
              onCancel: () => (result = false),
              onHidden: () => {
                resolve(result)
                nextTick(() => {
                  app.unmount()
                  root.remove()
                })
              }
            },
            { default: () => message }
          )
      })
      app.mount(root)
    })
  }
}
export { toastService } from './toast'
